import { useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import { SchoolLogin } from "./components/auth/SchoolLogin";
import { SchoolForgotPassword } from "./components/auth/SchoolForgotPassword";
import { ResetPassword } from "./components/auth/ResetPassword";
import { AuthCallback } from "./components/auth/AuthCallback";
import { TeacherLoginPage } from "./pages/TeacherLoginPage";
import TeacherPage from "./pages/TeacherPage";
import DemoLanding from "./pages/DemoLanding";
import DemoEntry from "./pages/DemoEntry";
import VerifyLightbox from "./pages/VerifyLightbox";
import { autoRestoreIfStale } from "./lib/demo/session";
import { ComingSoonGate } from "./components/app/ComingSoonGate";
import { HAS_APP, HAS_DEMO } from "./lib/site-mode";
import { ChunkErrorBoundary } from "./components/ChunkErrorBoundary";

const queryClient = new QueryClient();

// Watches for navigation OUT of /demo/* and auto-restores the user's real
// localStorage state. Prevents demo data from leaking into a real session
// if the user logs out (which lands them on /school/login) or types a
// non-demo URL in the address bar.
function DemoRestoreSentinel() {
  const location = useLocation();
  useEffect(() => {
    if (!location.pathname.startsWith('/demo')) {
      autoRestoreIfStale();
    }
  }, [location.pathname]);
  return null;
}

// A demo-only build has nothing to put behind the coming-soon gate, and the
// gate would only stop a prospect who followed the demo link.
const Gate = HAS_APP ? ComingSoonGate : ({ children }: { children: React.ReactNode }) => <>{children}</>;

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      {/* Development-stage site-wide gate — see ComingSoonGate.tsx. Remove
          this wrapper (and the import above) once the app is ready to launch
          publicly. */}
      <Gate>
        <BrowserRouter>
          <DemoRestoreSentinel />
          <ChunkErrorBoundary>
          <Routes>
            {/* The real app: not in the demo-only build, where every path
                that is not the demo lands on the demo. */}
            {HAS_APP ? (
              <>
                <Route path="/" element={<Index />} />
                <Route path="/school/login" element={<SchoolLogin />} />
                <Route path="/school/forgot-password" element={<SchoolForgotPassword />} />
                <Route path="/auth/reset-password" element={<ResetPassword />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route path="/teacher/login" element={<TeacherLoginPage />} />
                <Route path="/teacher" element={<TeacherPage />} />
              </>
            ) : (
              <Route path="/" element={<Navigate to="/demo" replace />} />
            )}
            {/* The demo: not in the app-only builds (production, UAT), where
                its paths go to the sign-in page. */}
            {HAS_DEMO ? (
              <>
                <Route path="/demo" element={<DemoLanding />} />
                <Route path="/demo/school" element={<DemoEntry role="school" />} />
                <Route path="/demo/teacher" element={<DemoEntry role="teacher" />} />
                <Route path="/demo/cover" element={<DemoEntry role="cover" />} />
              </>
            ) : (
              <Route path="/demo/*" element={<Navigate to="/" replace />} />
            )}
            {/* Verification harness — renders BlockMediaPreview with
                synthetic data so headless tests can confirm the lightbox
                opens in-app. Not linked from anywhere in the UI. */}
            <Route path="/verify-lightbox" element={<VerifyLightbox />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={HAS_APP ? <NotFound /> : <Navigate to="/demo" replace />} />
          </Routes>
          </ChunkErrorBoundary>
        </BrowserRouter>
      </Gate>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
